import { Button as MUIButton, Box, styled } from "@mui/material";
import { gray } from "configs/colors";
import { shadows } from "configs/shadows";
import { rem } from "utils";

import { ButtonProps } from "./Button.types";

export const Container = styled(MUIButton)<ButtonProps>(
  ({ size, color, variant, loading, theme: { palette } }) => {
    const { grey } = palette;
    let styleBasedOnType = {};
    const isOutlined = variant === "outlined";
    const sizeOverrides = size as ButtonProps["size"];
    const defaultIconSizeStyle = {
      minWidth: 0,
      minHeight: 0,
    };
    switch (sizeOverrides) {
      case "sm": {
        styleBasedOnType = {
          padding: isOutlined ? `${rem(7)} ${rem(13)}` : `${rem(8)} ${rem(14)}`,
        };
        break;
      }
      case "md": {
        styleBasedOnType = {
          padding: isOutlined
            ? `${rem(9)} ${rem(15)}`
            : `${rem(10)} ${rem(16)}`,
        };
        break;
      }
      case "lg": {
        styleBasedOnType = {
          padding: isOutlined
            ? `${rem(9)} ${rem(17)}`
            : `${rem(10)} ${rem(18)}`,
        };
        break;
      }
      case "xl": {
        styleBasedOnType = {
          padding: isOutlined
            ? `${rem(11)} ${rem(19)}`
            : `${rem(12)} ${rem(20)}`,
        };
        break;
      }
      case "2xl": {
        styleBasedOnType = {
          padding: isOutlined
            ? `${rem(15)} ${rem(27)}`
            : `${rem(16)} ${rem(28)}`,
        };
        break;
      }
      case "icon-sm": {
        styleBasedOnType = {
          ...defaultIconSizeStyle,
          /**
           * Design system: 8px
           * Adjustment: 7px
           * Explanation: The Button's style has no "width/height" by default, so the "box-sizing: border-box" does not work.
           */
          padding: isOutlined ? rem(7) : rem(8),
        };
        break;
      }
      case "icon-md": {
        styleBasedOnType = {
          ...defaultIconSizeStyle,
          /**
           * Design system: 10px
           * Adjustment: 9px
           * Explanation: The Button's style has no "width/height" by default, so the "box-sizing: border-box" does not work.
           */
          padding: isOutlined ? rem(9) : rem(10),
        };
        break;
      }
      case "icon-lg": {
        styleBasedOnType = {
          ...defaultIconSizeStyle,
          /**
           * Design system: 12px
           * Adjustment: 11px
           * Explanation: The Button's style has no "width/height" by default, so the "box-sizing: border-box" does not work.
           */
          padding: isOutlined ? rem(11) : rem(12),
        };
        break;
      }
      case "icon-xl": {
        styleBasedOnType = {
          ...defaultIconSizeStyle,
          /**
           * Design system: 14px
           * Adjustment: 13px
           * Explanation: The Button's style has no "width/height" by default, so the "box-sizing: border-box" does not work.
           */
          padding: isOutlined ? rem(13) : rem(14),
        };
        break;
      }
      case "icon-2xl": {
        styleBasedOnType = {
          ...defaultIconSizeStyle,
          /**
           * Design system: 16px
           * Adjustment: 15px
           * Explanation: The Button's style has no "width/height" by default, so the "box-sizing: border-box" does not work.
           */
          padding: isOutlined ? rem(15) : rem(16),
        };
        break;
      }
      default:
        break;
    }
    const boxShadowPaletteColor = color
      ? ((color === "inherit" ? grey : palette[color]) as unknown as Record<
          number,
          string
        >)
      : null;

    return {
      position: "relative",
      ...styleBasedOnType,

      // Override "box-shadow"
      "&:not(.MuiButton-text):not(.MuiButton-disableElevation)": {
        "&, &:hover": {
          boxShadow: shadows.xs,
        },
        "&:active": {
          boxShadow: boxShadowPaletteColor
            ? `0px 0px 0px 4px ${boxShadowPaletteColor[100]}, 0px 1px 2px 0px rgba(16, 24, 40, 0.05)`
            : undefined,
        },
      },

      // Override "outlined" variant and "inherit" color
      "&:not(.Mui-disabled).MuiButton-outlinedInherit": {
        color: gray[700],
        borderColor: gray[300],
      },

      ".MuiButton-startIcon, .MuiButton-endIcon": {
        opacity: loading ? 0 : 1,
        transition: "opacity 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
      },
    };
  }
);

export const LoadingWrapper = styled(Box)(() => ({
  display: "inline-flex",
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
}));

export const ContentWrapper = styled(Box)<Pick<ButtonProps, "loading">>(
  ({ loading }) => ({
    display: "inline-flex",
    justifyContent: "center",
    alignItems: "center",
    opacity: loading ? 0 : 1,
    transition: "opacity 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",

    "& > *": {
      color: "inherit",
    },
  })
);
